import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Banner, BlockStack, Box, Button, Card, IndexTable, InlineStack, Modal, Select, Tag, Text, TextField } from '@shopify/polaris';
import { cacApi } from './apiClient';
import { EditIcon } from '@shopify/polaris-icons';
import { confirmText, exactText, readParams, rows, sortedFound, sortedRows, writeParams } from './dictionaryValues';

const liveRun = (r) => r && (r.status === 'queued' || r.status === 'running');

// The progress line for the rewrite run a Dictionary action started: polls until it finishes.
function RewriteProgress({ runId, onDone }) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    if (!runId) return undefined;
    let stop = false;
    const tick = () => cacApi.run(runId).then((r) => {
      if (stop) return;
      setRun(r);
      if (liveRun(r)) setTimeout(tick, 3000); else onDone();
    }).catch(() => { if (!stop) setTimeout(tick, 5000); });
    tick();
    return () => { stop = true; };
  }, [runId, onDone]);
  if (!runId || !run) return null;
  if (run.status === 'queued') return <Banner tone="info"><p>Waiting for the worker to update {run.product_count || 0} products… (it runs after any analysis already in progress)</p></Banner>;
  if (liveRun(run)) return <Banner tone="info"><p>Updating products: {run.done_count || 0} of {run.product_count || 0}…</p></Banner>;
  if (run.status === 'completed') return <Banner tone={run.failed_count ? 'warning' : 'success'}><p>{run.note}{run.failed_count ? ' (see the Runs tab and each product\'s Writes for what Shopify said)' : ''}</p></Banner>;
  if (run.status === 'cancelled') return <Banner tone="warning"><p>Cancelled: {run.note || 'the update was stopped'}.</p></Banner>;
  return <Banner tone="critical"><p>The update stopped: {run.error || run.status}. See the Runs tab.</p></Banner>;
}

// The chips of one accepted value's current values, with × and an Add field.
function CurrentValues({ facet, value, current, onAdd, onRemove }) {
  const [text, setText] = useState('');
  const submit = () => { if (text.trim()) { onAdd(text.trim()); setText(''); } };
  return (
    <BlockStack gap="100">
      <InlineStack gap="100" wrap>
        {current.map((a) => <Tag key={a} onRemove={() => onRemove(a)}>{a}</Tag>)}
      </InlineStack>
      <TextField label={`Add a current value of ${value}`} labelHidden placeholder="Add a spelling found on products" value={text} onChange={setText}
        autoComplete="off" size="slim" connectedRight={<Button onClick={submit} disabled={!text.trim()}>Add</Button>} />
    </BlockStack>
  );
}

export default function DictionaryTab({ dictionary, reloadDictionary, notify }) {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  // The view (source filter, facet, sort) lives in the address, so any view is a link to share.
  const [sp, setSp] = useSearchParams();
  const { src, facet, sort, found: foundSort } = useMemo(() => readParams(sp), [sp]);   // src: all | hub | taxonomy | found; sort col 0 value, 1 products, 2 current values
  const setParams = useCallback((changes) => setSp((prev) => writeParams(prev, changes), { replace: true }), [setSp]);
  const setSrc = (v) => setParams({ src: v });
  const setFacet = useCallback((v) => setParams({ facet: v }), [setParams]);
  const setSort = (v) => setParams({ sort: v });
  const setFoundSort = (v) => setParams({ found: v });
  const TABLE_COLS = [0, 2, 1];   // table column index -> sort col (Accepted value, Current values, On products)
  const [counts, setCounts] = useState({});
  const [adding, setAdding] = useState(null);     // {text}
  const [renaming, setRenaming] = useState(null); // {value, text}
  const [confirm, setConfirm] = useState(null);   // {action, facet, value, next, products, multi, label}
  const [foldInto, setFoldInto] = useState({});   // key -> chosen accepted value
  const [runId, setRunId] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPending = useCallback(() => cacApi.pending().then((r) => setPending(r.values)).catch((e) => setError(e.message)), []);
  const loadCounts = useCallback((f) => { if (f) cacApi.valueCounts(f).then((r) => setCounts(r.counts || {})).catch(() => setCounts({})); }, []);
  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => { loadCounts(facet); }, [facet, loadCounts]);

  const found = src === 'found';
  const facets = useMemo(() => (dictionary || []).filter((f) => f.id !== 'category' && (src === 'all' || found || (f.source || 'hub') === src)), [dictionary, src, found]);
  useEffect(() => {
    if (!found && facets.length && !facets.some((f) => f.id === facet)) setFacet((facets.find((f) => f.values.length) || facets[0]).id);
  }, [facets, facet, found, setFacet]);
  const cur = facets.find((f) => f.id === facet);
  const byId = (id) => (dictionary || []).find((f) => f.id === id) || {};
  const labelOf = (id) => byId(id).label || id;
  const shopifyOwned = (f) => f && f.kind === 'standard';
  const SRC = [['all', 'All facets'], ['hub', 'Soccer Post'], ['taxonomy', 'Shopify attributes'], ['found', `Found on products, not yet accepted (${(pending || []).length})`]];

  const refresh = useCallback(() => { reloadDictionary(); loadPending(); loadCounts(facet); }, [reloadDictionary, loadPending, loadCounts, facet]);
  const onRunDone = useCallback(() => { loadCounts(facet); }, [loadCounts, facet]);
  const fail = (e) => notify(e.message, true);
  const started = (r, text) => { notify(text); if (r && r.run_id) setRunId(r.run_id); refresh(); };

  const addValue = async () => {
    const t = (adding.text || '').trim();
    if (!t) return;
    try { await cacApi.addValue(facet, t); notify(`Added ${labelOf(facet)} value '${t}'`); setAdding(null); refresh(); } catch (e) { fail(e); }
  };
  // Opens the confirmation with the number of products that hold the value on Shopify (fetched for another facet).
  const ask = async (action, f, value, next, products) => {
    let n = products;
    if (n == null) {
      try { n = ((await cacApi.valueCounts(f)).counts || {})[exactText(value)] || 0; } catch (e) { n = 0; }
    }
    setConfirm({ action, facet: f, value, next, products: n, multi: !!byId(f).multi, label: labelOf(f) });
  };
  const run = async () => {
    const c = confirm;
    setBusy(true);
    try {
      if (c.action === 'rename') {
        const r = await cacApi.renameValue(c.facet, c.value, c.next);
        setRenaming(null);
        started(r, `Renamed '${c.value}' to '${c.next}' (${r.products} products to update)`);
      } else if (c.action === 'remove') {
        const r = await cacApi.removeValue(c.facet, c.value);
        started(r, `Removed '${c.value}' (${r.products} products to update)`);
      } else if (c.action === 'delete') {
        await cacApi.removeValue(c.facet, c.value);
        notify(`Deleted '${c.value}'`); refresh();
      } else {
        const r = await cacApi.addAlias(c.facet, c.value, c.next);
        started(r, `'${c.value}' is now written as '${c.next}' (${r.products} products to update${r.requeued ? `, ${r.requeued} re-analysed` : ''})`);
      }
      setConfirm(null);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const removeCurrent = async (alias) => {
    try { await cacApi.deleteAlias(facet, alias); notify(`'${alias}' is no longer a current value`); refresh(); } catch (e) { fail(e); }
  };
  const bringBack = async (value) => {
    try { await cacApi.setValueStatus(facet, value, 'allowed'); notify(`'${value}' is in use again`); refresh(); } catch (e) { fail(e); }
  };
  const accept = async (p) => {
    try { const r = await cacApi.setValueStatus(p.facet, p.value, 'allowed'); notify(`Accepted '${p.value}' (${r.requeued} products re-analysed)`); refresh(); } catch (e) { fail(e); }
  };
  const countOf = (f, value) => (f === facet ? counts[exactText(value)] || 0 : null);   // null: fetched when asked

  const list = cur ? sortedRows(rows(cur, counts), sort) : [];
  const accepted = (f) => (byId(f).values || []).filter((v) => v.status === 'allowed').map((v) => v.value);

  return (
    <BlockStack gap="400">
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      <RewriteProgress runId={runId} onDone={onRunDone} />

      <InlineStack gap="200">
        {SRC.map(([k, t]) => <Button key={k} size="slim" pressed={src === k} onClick={() => setSrc(k)}>{t}</Button>)}
      </InlineStack>

      {!found && <Card padding="0">
        <Box padding="400">
          <BlockStack gap="300">
            <BlockStack gap="100"><Text as="h3" variant="headingSm">Facets and values</Text>
              <Text as="p" tone="subdued">A facet is one kind of detail, such as Colour, Tournament or Vendor. Each <b>accepted value</b> is what the Hub writes on products, spelled exactly as shown. Its <b>current values</b> are the other spellings found on products, which get overwritten with the accepted one. <b>Rename</b> an accepted value and every product that holds it is updated. <b>Remove</b> one and it is taken off every product and never proposed again. <b>Shopify attributes</b> come from Shopify's own list, so their values cannot be renamed; one can be merged into another value of the list (the pencil), which rewrites the products that hold it, and an entry the shop added to the list in Shopify can be added here. Vendor: Shopify treats "Adidas" and "adidas" as two different vendors, so the accepted spelling is what every product gets.</Text></BlockStack>
            <InlineStack gap="300" align="space-between" blockAlign="end">
              <Select label={cur && cur.multi ? 'Facet (list)' : 'Facet'} options={facets.map((f) => ({ value: f.id, label: `${f.label || f.id}${f.multi ? ' · list' : ''}` }))} value={facet} onChange={setFacet} />
              <Button onClick={() => setAdding({ text: '' })} disabled={!cur}>Add accepted value…</Button>
            </InlineStack>
            {cur && <Text as="p" variant="bodySm" tone="subdued">{cur.source === 'taxonomy' || shopifyOwned(cur) ? `Shopify attribute (${cur.namespace || 'shopify'}.${cur.key || cur.id})` : cur.kind === 'vendor' ? 'The product vendor' : `Soccer Post (soccer_meta.${cur.key || cur.id})`}{cur.multi ? ' · Multi: a product can hold several values' : ''}{shopifyOwned(cur) ? ' · Shopify owns this list' : ''}</Text>}
          </BlockStack>
        </Box>
        <IndexTable resourceName={{ singular: 'value', plural: 'values' }} itemCount={list.length} selectable={false}
          headings={[{ title: 'Accepted value (what we will write)' }, { title: 'Current values (will overwrite)' }, { title: 'On products', alignment: 'end' }, { title: '' }]}
          sortable={[true, true, true, false]} sortColumnIndex={TABLE_COLS.indexOf(sort.col)} sortDirection={sort.dir}
          onSort={(col, dir) => setSort({ col: TABLE_COLS[col], dir })}
          emptyState={<Box padding="400"><Text as="p" tone="subdued">No values yet</Text></Box>}>
          {list.map((v, i) => {
            const gone = v.status === 'rejected' || v.status === 'retired';
            const editing = renaming && renaming.value === v.value;
            return (
              <IndexTable.Row id={v.value} key={v.value} position={i}>
                <IndexTable.Cell>
                  {editing ? (
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      {shopifyOwned(cur)
                        ? <Select label="Merge into" labelHidden options={[{ value: '', label: 'Merge into…' }, ...accepted(facet).filter((x) => x !== v.value).map((x) => ({ value: x, label: x }))]}
                            value={renaming.text === v.value ? '' : renaming.text} onChange={(t) => setRenaming({ ...renaming, text: t })} />
                        : <TextField label="New spelling" labelHidden value={renaming.text} onChange={(t) => setRenaming({ ...renaming, text: t })} autoComplete="off" size="slim" autoFocus />}
                      <Button size="slim" variant="primary" disabled={!renaming.text.trim() || renaming.text.trim() === v.value}
                        onClick={() => ask('rename', facet, v.value, renaming.text.trim(), v.products)}>{shopifyOwned(cur) ? 'Merge' : 'Save'}</Button>
                      <Button size="slim" onClick={() => setRenaming(null)}>Cancel</Button>
                    </InlineStack>
                  ) : (
                    <InlineStack gap="200" blockAlign="center" wrap>
                      {v.status === 'retired'
                        ? <Text as="span" tone="subdued" textDecorationLine="line-through">{v.value}</Text>
                        : <Text as="span" tone={gone ? 'subdued' : undefined} fontWeight={gone ? undefined : 'semibold'}>{v.value}</Text>}
                      {v.status === 'pending' && <Badge tone="attention">found, not accepted</Badge>}
                      {v.status === 'rejected' && <Badge>not used</Badge>}
                      {!gone && <Button variant="tertiary" size="micro" icon={EditIcon} accessibilityLabel={`${shopifyOwned(cur) ? 'Merge' : 'Rename'} ${v.value}`} onClick={() => setRenaming({ value: v.value, text: v.value })} />}
                    </InlineStack>
                  )}
                  {v.status === 'retired' && <Text as="p" variant="bodySm" tone="subdued">Shopify no longer offers this value</Text>}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {gone ? (
                    v.products > 0 ? (
                      <InlineStack gap="200" blockAlign="center">
                        <Select label="Fold into" labelHidden options={[{ value: '', label: 'Fold into…' }, ...accepted(facet).filter((x) => x !== v.value).map((x) => ({ value: x, label: x }))]}
                          value={foldInto[v.value] || ''} onChange={(x) => setFoldInto({ ...foldInto, [v.value]: x })} />
                        <Button size="slim" disabled={!foldInto[v.value]} onClick={() => ask('fold', facet, v.value, foldInto[v.value], v.products)}>Fold</Button>
                      </InlineStack>
                    ) : <Text as="span" tone="subdued">—</Text>
                  ) : (
                    <CurrentValues facet={facet} value={v.value} current={v.current}
                      onAdd={(a) => ask('fold', facet, a, v.value, countOf(facet, a))} onRemove={removeCurrent} />
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <BlockStack gap="0" inlineAlign="end">
                    <Text as="span" alignment="end" numeric>{v.products}</Text>
                    {v.stillCurrent > 0 && <Text as="span" variant="bodySm" tone="subdued" alignment="end">{v.stillCurrent} still hold a current value</Text>}
                  </BlockStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack gap="300" blockAlign="center">
                    {v.status === 'rejected' && <Button variant="tertiary" size="slim" onClick={() => bringBack(v.value)}>Use again</Button>}
                    {v.status === 'rejected' && cur.kind === 'vendor' && v.products === 0 && <Button variant="tertiary" tone="critical" size="slim" onClick={() => ask('delete', facet, v.value, null, 0)}>Delete</Button>}
                    {v.status === 'pending' && <Button variant="tertiary" size="slim" onClick={() => accept({ facet, value: v.value })}>Accept</Button>}
                    {!gone && cur.kind !== 'vendor' && <Button variant="tertiary" tone="critical" size="slim" onClick={() => ask('remove', facet, v.value, null, v.products)}>Remove</Button>}
                  </InlineStack>
                </IndexTable.Cell>
              </IndexTable.Row>
            );
          })}
        </IndexTable>
      </Card>}

      {found && <Card padding="0">
        <Box padding="400"><BlockStack gap="100">
          <InlineStack gap="200"><Text as="h3" variant="headingSm">Found on products, not yet accepted</Text><Badge tone="attention">{String((pending || []).length)}</Badge></InlineStack>
          <Text as="p" tone="subdued">Words the analysis found on products that are not an accepted value of any facet yet, so the Hub will not write them until you decide. <b>Accept</b> makes it an accepted value in its own right. <b>Fold into</b> makes it a current value of the one you pick and rewrites the products that hold it. <b>Remove</b> takes it off the products and stops it being proposed.</Text>
        </BlockStack></Box>
        <IndexTable resourceName={{ singular: 'found value', plural: 'found values' }} itemCount={(pending || []).length} selectable={false}
          headings={[{ title: 'Facet' }, { title: 'Value found' }, { title: 'On products', alignment: 'end' }, { title: 'Decide' }]}
          sortable={[true, true, true, false]} sortColumnIndex={foundSort.col} sortDirection={foundSort.dir}
          onSort={(col, dir) => setFoundSort({ col, dir })}
          emptyState={<Box padding="400"><Text as="p" tone="subdued">Nothing waiting.</Text></Box>}>
          {sortedFound(pending || [], foundSort, labelOf).map((p, i) => {
            const k = `${p.facet}|${p.value}`;
            const options = accepted(p.facet);
            return (
              <IndexTable.Row id={k} key={k} position={i}>
                <IndexTable.Cell>{labelOf(p.facet)}</IndexTable.Cell>
                <IndexTable.Cell><Text as="span" fontWeight="semibold">{p.value}</Text></IndexTable.Cell>
                <IndexTable.Cell><Text as="span" alignment="end" numeric>{p.product_count}</Text></IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack gap="200" blockAlign="center" wrap>
                    {!shopifyOwned(byId(p.facet)) && <Button size="slim" onClick={() => accept(p)}>Accept as new value</Button>}
                    <Select label="Fold into" labelHidden options={[{ value: '', label: 'Fold into…' }, ...options.map((x) => ({ value: x, label: x }))]}
                      value={foldInto[k] || ''} onChange={(x) => setFoldInto({ ...foldInto, [k]: x })} />
                    <Button size="slim" disabled={!foldInto[k]} onClick={() => ask('fold', p.facet, p.value, foldInto[k], countOf(p.facet, p.value))}>Fold</Button>
                    {byId(p.facet).kind !== 'vendor' && <Button size="slim" tone="critical" onClick={() => ask('remove', p.facet, p.value, null, countOf(p.facet, p.value))}>Remove</Button>}
                  </InlineStack>
                </IndexTable.Cell>
              </IndexTable.Row>
            );
          })}
        </IndexTable>
      </Card>}

      {adding && (
        <Modal open onClose={() => setAdding(null)} title={`Add a ${labelOf(facet)} value`}
          primaryAction={{ content: 'Add', onAction: addValue, disabled: !adding.text.trim() }} secondaryActions={[{ content: 'Cancel', onAction: () => setAdding(null) }]}>
          <Modal.Section>
            <BlockStack gap="200">
              <TextField label="Accepted value (what we will write)" value={adding.text} onChange={(t) => setAdding({ text: t })} autoComplete="off" autoFocus />
              {shopifyOwned(cur) && <Text as="p" tone="subdued" variant="bodySm">Shopify owns this list, so the value must already be an entry of it: add the entry in Shopify first (Content › Metaobjects › {labelOf(facet)} › Add entry; not the product metafield definition page, which has no entries), then add it here and it is accepted with the entry's spelling.</Text>}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
      {confirm && (
        <Modal open onClose={() => setConfirm(null)}
          title={confirm.action === 'rename' ? `${shopifyOwned(byId(confirm.facet)) ? 'Merge' : 'Rename'} "${confirm.value}" ${shopifyOwned(byId(confirm.facet)) ? 'into' : 'to'} "${confirm.next}"?` : confirm.action === 'remove' ? `Remove "${confirm.value}" from ${confirm.label}?` : confirm.action === 'delete' ? `Delete "${confirm.value}" from this list?` : `Write "${confirm.value}" as "${confirm.next}"?`}
          primaryAction={{
            content: confirm.action === 'rename' ? `Rename and update ${confirm.products} products` : confirm.action === 'remove' ? `Remove and update ${confirm.products} products` : confirm.action === 'delete' ? 'Delete' : `Fold and update ${confirm.products} products`,
            destructive: confirm.action === 'remove' || confirm.action === 'delete', loading: busy, onAction: run,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setConfirm(null), disabled: busy }]}>
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p">{confirmText(confirm.action, confirm.label, confirm.value, confirm.next, confirm.products || 0, confirm.multi)}</Text>
              {confirm.action !== 'delete' && <Text as="p" tone="subdued" variant="bodySm">Products are updated on Shopify right after this, one at a time in the background. Each one is logged under the product's Writes.</Text>}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}
